import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";


import { 
  User, 
  Shield, 
  Camera, 
  Save, 
  MapPin, 
  Globe, 
  Phone, 
  UserCog, 
  Crown, 
  Users, 
  Trash2,
  Mail as MailIcon,
  TestTube
} from "lucide-react";

export default function Profile() {
  const { user, refetchUser } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // State management
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailMessage, setTestEmailMessage] = useState('');
  
  // Profile form schema
  const profileSchema = z.object({
    firstName: z.string().min(1, t('common.firstNameRequired')),
    lastName: z.string().min(1, t('common.lastNameRequired')),
    email: z.string().email(t('common.invalidEmail')),
    bio: z.string().optional(),
    location: z.string().optional(),
    website: z.string().url().optional().or(z.literal("")),
    phoneNumber: z.string().optional(),
  });

  type ProfileFormData = z.infer<typeof profileSchema>;
  
  const logout = () => {
    window.location.href = '/api/logout';
  };

  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    setTestEmailMessage('');
    
    try {
      const response = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user?.email || 'test@example.com',
          firstName: user?.firstName || 'Usuario de Prueba'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setTestEmailMessage(`✅ Email de prueba enviado a ${data.email}`);
        toast({
          title: "Email enviado",
          description: `Email de verificación enviado a ${data.email}`,
          variant: "success",
        });
        
        // Log details to console for admin
        // console.log('📧 Test Email Details:', {
          // email: data.email,
          // token: data.testToken,
          // verificationUrl: data.verificationUrl
        // }); // Removed for production
      } else {
        setTestEmailMessage(`❌ Error: ${data.message}`);
        toast({
          title: "Error",
          description: data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error testing email:', error);
      setTestEmailMessage('❌ Error de conexión');
      toast({
        title: "Error",
        description: "Error de conexión al probar el email",
        variant: "destructive",
      });
    } finally {
      setTestEmailLoading(false);
    }
  };

  // Check for password change success message
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('passwordChanged') === 'true') {
      toast({
        title: t('common.passwordChanged'),
        description: t('common.passwordUpdated'),
      });
      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      bio: user?.bio || "",
      location: user?.location || "",
      website: user?.website || "",
      phoneNumber: user?.phoneNumber || "",
    },
  });

  // Profile update mutation
  const updateProfile = useMutation({
    mutationFn: (data: ProfileFormData) => apiRequest("PATCH", "/api/auth/user", data),
    onSuccess: () => {
      toast({
        title: t('common.profileUpdated'),
        description: t('common.profileSaved'),
      });
      refetchUser();
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error.message || t('common.errorUpdatingProfile'),
        variant: "destructive",
      });
    },
  });

  // Role update mutation (admin only)
  const updateUserRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => 
      apiRequest("PATCH", `/api/auth/user-role`, { userId, role }),
    onSuccess: () => {
      toast({
        title: t('common.roleUpdated'),
        description: t('common.accountTypeChanged'),
      });
      refetchUser();
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error.message || t('common.errorChangingRole'),
        variant: "destructive",
      });
    },
  });

  // Account deletion mutation
  const deleteAccount = useMutation({
    mutationFn: () => apiRequest("DELETE", "/api/auth/delete-account"),
    onSuccess: () => {
      // Clear all React Query cache to ensure fresh data everywhere
      queryClient.clear();
      
      // Clear all localStorage data
      localStorage.clear();
      
      // Clear all sessionStorage data
      sessionStorage.clear();
      
      // Clear all cookies manually by getting all cookie names and clearing them
      const cookies = document.cookie.split(";");
      cookies.forEach(cookie => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        
        // Clear cookie for current domain and path
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
        
        // Also try clearing with secure and samesite attributes
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure;samesite=strict`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure;samesite=lax`;
      });
      
      // Clear specific common authentication cookies
      const authCookies = ['connect.sid', 'session', 'auth', 'user', 'token', 'jwt'];
      authCookies.forEach(cookieName => {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
      });
      
      toast({
        title: t('common.accountDeleted'),
        description: t('common.accountDataDeleted'),
      });
      
      // Force complete page reload to reset everything
      setTimeout(() => {
        // Use replace to ensure no history is kept
        window.location.replace("/");
      }, 1500);
    },
    onError: (error: any) => {
      toast({
        title: t('common.error'),
        description: error.message || t('common.errorDeletingAccount'),
        variant: "destructive",
      });
    },
  });

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast({
        title: t('common.fileTooLarge'),
        description: t('common.fileSizeLimit'),
        variant: "destructive",
      });
      return;
    }

    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      toast({
        title: t('common.invalidFileType'),
        description: t('common.fileTypeLimit'),
        variant: "destructive",
      });
      return;
    }

    setIsUploadingImage(true);

    // Convert file to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const imageData = e.target?.result as string;
        
        const response = await fetch('/api/auth/upload-avatar', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ imageData }),
        });

        const data = await response.json();
        
        if (response.ok) {
                toast({
        title: t('common.photoUpdated'),
        description: t('common.profilePhotoChanged'),
      });
          refetchUser();
        } else {
          throw new Error(data.message || t('common.errorUploadingImage'));
        }
      } catch (error: any) {
              toast({
        title: t('common.error'),
        description: error.message || t('common.errorUploadingImage'),
        variant: "destructive",
      });
      } finally {
        setIsUploadingImage(false);
      }
    };

    reader.onerror = () => {
      toast({
        title: t('common.error'),
        description: t('common.errorReadingFile'),
        variant: "destructive",
      });
      setIsUploadingImage(false);
    };

    reader.readAsDataURL(file);
  };

  const onProfileSubmit = (data: ProfileFormData) => {
    updateProfile.mutate(data);
  };

  const handleDeleteAccount = () => {
    deleteAccount.mutate();
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'premium': return <Shield className="w-4 h-4 text-purple-600" />;
      default: return <Users className="w-4 h-4 text-blue-600" />;
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin': return <Badge className="bg-yellow-100 text-yellow-800">{t('profile.accountManagement.roles.admin')}</Badge>;
      case 'premium': return <Badge className="bg-yellow-100 text-yellow-800">{t('profile.accountManagement.roles.premium')}</Badge>;
      case 'user': return <Badge className="bg-blue-100 text-blue-800">{t('profile.accountManagement.roles.user')}</Badge>;
      default: return <Badge variant="secondary">{t('profile.accountManagement.roles.guest')}</Badge>;
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            {t('profile.title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mt-2">
            {t('profile.subtitle')}
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-8">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile">
              <User className="w-4 h-4 mr-2" />
              {t('profile.tabs.profile')}
            </TabsTrigger>
            <TabsTrigger value="account">
              <Shield className="w-4 h-4 mr-2" />
              {t('profile.tabs.account')}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  {t('profile.personalInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
                  {/* Profile Image Section */}
                  <div className="flex items-center space-x-6 p-6 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="relative">
                      <Avatar className="h-20 w-20">
                        <AvatarImage src={user?.profileImageUrl || undefined} />
                        <AvatarFallback className="text-xl">
                          {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <label 
                        htmlFor="avatar-upload" 
                        className="absolute -bottom-1 -right-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 cursor-pointer transition-colors"
                      >
                        <Camera className="w-4 h-4" />
                      </label>
                      <input
                        id="avatar-upload"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={isUploadingImage}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {getRoleIcon(user?.role || 'user')}
                        <span>{getRoleBadge(user?.role || 'user')}</span>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        {t('profile.profileImage.changePhoto')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {t('profile.profileImage.fileInfo')}
                      </p>
                      {isUploadingImage && (
                        <div className="flex items-center mt-2">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                          <span className="text-xs text-slate-600 dark:text-slate-300">{t('profile.profileImage.uploading')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">{t('profile.form.firstName')}</Label>
                      <Input
                        id="firstName"
                        {...profileForm.register("firstName")}
                        className="mt-1"
                      />
                      {profileForm.formState.errors.firstName && (
                        <p className="text-sm text-red-600 mt-1">
                          {profileForm.formState.errors.firstName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="lastName">{t('profile.form.lastName')}</Label>
                      <Input
                        id="lastName"
                        {...profileForm.register("lastName")}
                        className="mt-1"
                      />
                      {profileForm.formState.errors.lastName && (
                        <p className="text-sm text-red-600 mt-1">
                          {profileForm.formState.errors.lastName.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                                          <Label htmlFor="email">{t('profile.form.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      {...profileForm.register("email")}
                      className="mt-1"
                    />
                    {profileForm.formState.errors.email && (
                      <p className="text-sm text-red-600 mt-1">
                        {profileForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                                          <Label htmlFor="bio">{t('profile.form.bio')}</Label>
                    <Textarea
                      id="bio"
                      {...profileForm.register("bio")}
                                              placeholder={t('profile.form.bioPlaceholder')}
                      className="mt-1"
                      rows={3}
                    />
                    {profileForm.formState.errors.bio && (
                      <p className="text-sm text-red-600 mt-1">
                        {profileForm.formState.errors.bio.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="location">
                        <MapPin className="w-4 h-4 inline mr-1" />
                        {t('profile.form.location')}
                      </Label>
                      <Input
                        id="location"
                        {...profileForm.register("location")}
                        placeholder={t('profile.form.locationPlaceholder')}
                        className="mt-1"
                      />
                    </div>

                    {/* Website field temporarily hidden */}
                    {/* <div>
                      <Label htmlFor="website">
                        <Globe className="w-4 h-4 inline mr-1" />
                        {t('profile.form.website')}
                      </Label>
                      <Input
                        id="website"
                        type="url"
                        {...profileForm.register("website")}
                        placeholder={t('profile.form.websitePlaceholder')}
                        className="mt-1"
                      />
                    </div> */}
                  </div>

                  <div>
                    <Label htmlFor="phoneNumber">
                                              <Phone className="w-4 h-4 inline mr-1" />
                        {t('profile.form.phone')}
                      </Label>
                      <Input
                        id="phoneNumber"
                        {...profileForm.register("phoneNumber")}
                        placeholder={t('profile.form.phonePlaceholder')}
                      className="mt-1"
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={updateProfile.isPending}
                      className="flex items-center"
                    >
                      {updateProfile.isPending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          {t('profile.form.saving')}
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {t('profile.form.saveChanges')}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            {/* Account Type Management */}
            {user?.role === 'admin' && (
              <Card>
                <CardHeader>
                                      <CardTitle className="flex items-center">
                      <UserCog className="w-5 h-5 mr-2" />
                      {t('profile.accountManagement.title')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="role-select">{t('profile.accountManagement.changeAccountType')}</Label>
                      <Select
                        defaultValue={user?.role || 'user'}
                        onValueChange={(value) => updateUserRole.mutate({ userId: user.id, role: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar tipo de cuenta" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="guest">Invitado</SelectItem>
                          <SelectItem value="user">Usuario</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                          <SelectItem value="admin">Administrador</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                        Se requiere acceso de administrador para cambiar tipos de cuenta
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}


            {/* Admin Tools */}
            {user?.role === 'admin' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TestTube className="w-5 h-5 mr-2" />
                    Herramientas de Administrador
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Herramientas para probar y administrar el sistema
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                        Probar Sistema de Email
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                        Envía un email de prueba para verificar que el sistema de verificación funciona correctamente.
                      </p>
                      
                      <div className="space-y-3">
                        {/* Simple email test - no auth required */}
                        <Button 
                          onClick={async () => {
                            setTestEmailLoading(true);
                            setTestEmailMessage('');
                            
                            try {
                              const response = await fetch('/api/test-email-simple', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  email: user?.email || 'test@example.com',
                                  firstName: user?.firstName || 'Usuario de Prueba'
                                }),
                              });

                              const data = await response.json();

                              if (response.ok) {
                                setTestEmailMessage(`✅ Email de prueba enviado a ${data.email}`);
                                toast({
                                  title: "Email enviado",
                                  description: `Email de verificación enviado a ${data.email}`,
                                  variant: "success",
                                });
                                
                                // Log details to console
                                // console.log('📧 Test Email Details:', {
                                  // email: data.email,
                                  // token: data.testToken,
                                  // verificationUrl: data.verificationUrl
                                // }); // Removed for production
                              } else {
                                setTestEmailMessage(`❌ Error: ${data.message}`);
                                toast({
                                  title: "Error",
                                  description: data.message,
                                  variant: "destructive",
                                });
                              }
                            } catch (error) {
                              console.error('Error testing email:', error);
                              setTestEmailMessage('❌ Error de conexión');
                              toast({
                                title: "Error",
                                description: "Error de conexión al probar el email",
                                variant: "destructive",
                              });
                            } finally {
                              setTestEmailLoading(false);
                            }
                          }}
                          disabled={testEmailLoading}
                          variant="outline"
                          className="flex items-center bg-green-50 border-green-200"
                        >
                          {testEmailLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <MailIcon className="w-4 h-4 mr-2" />
                              🚀 Probar Email (Sin Auth)
                            </>
                          )}
                        </Button>
                        
                        <Button 
                          onClick={handleTestEmail}
                          disabled={testEmailLoading}
                          variant="outline"
                          className="flex items-center"
                        >
                          {testEmailLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <MailIcon className="w-4 h-4 mr-2" />
                              Enviar Email de Prueba (Con Auth)
                            </>
                          )}
                        </Button>
                        
                        {testEmailMessage && (
                          <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                            <p className="text-sm font-medium">{testEmailMessage}</p>
                          </div>
                        )}
                        
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          <p>📧 Email se enviará a: <strong>{user?.email}</strong></p>
                          <p>🔗 Verifica la consola del navegador para ver el token y URL de verificación</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Email Verification Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MailIcon className="w-5 h-5 mr-2" />
                  Estado de Verificación de Email
                </CardTitle>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Verifica tu email para acceder a todas las funciones
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {user?.isEmailVerified ? (
                    <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-medium text-green-800 dark:text-green-200">
                          Email Verificado
                        </h3>
                        <p className="text-sm text-green-600 dark:text-green-300">
                          Tu email {user.email} está verificado. Tienes acceso completo a todas las funciones.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                        <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
                            Email No Verificado
                          </h3>
                          <p className="text-sm text-yellow-600 dark:text-yellow-300">
                            Tu email {user?.email} no está verificado. Algunas funciones pueden estar limitadas.
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Button 
                          onClick={async () => {
                            try {
                              const response = await fetch('/api/auth/resend-verification', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  email: user?.email
                                }),
                              });

                              const data = await response.json();

                              if (response.ok) {
                                toast({
                                  title: "Email enviado",
                                  description: "Se ha enviado un nuevo email de verificación a tu dirección",
                                  variant: "success",
                                });
                              } else {
                                toast({
                                  title: "Error",
                                  description: data.message || "Error al enviar el email de verificación",
                                  variant: "destructive",
                                });
                              }
                            } catch (error) {
                              console.error('Error sending verification email:', error);
                              toast({
                                title: "Error",
                                description: "Error de conexión al enviar el email",
                                variant: "destructive",
                              });
                            }
                          }}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <MailIcon className="w-4 h-4 mr-2" />
                          Enviar Email de Verificación
                        </Button>
                        
                        <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                          Revisa tu bandeja de entrada y carpeta de spam. El enlace expira en 24 horas.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Delete Account */}
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader>
                <CardTitle className="flex items-center text-red-600 dark:text-red-400">
                  <Trash2 className="w-5 h-5 mr-2" />
                  Zona Peligrosa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                      Eliminar Cuenta
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                      Una vez eliminada, tu cuenta no se puede recuperar. Esta acción eliminará permanentemente todos tus datos.
                    </p>
                  </div>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="flex items-center">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar Mi Cuenta
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Estás absolutamente seguro?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción no se puede deshacer. Esto eliminará permanentemente tu cuenta
                          y todos los datos asociados con ella.
                          <br /><br />
                          <strong>Si tienes una suscripción activa, se cancelará automáticamente al final 
                          del período de facturación actual.</strong> No se realizarán más cobros.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteAccount}
                          className="bg-red-600 hover:bg-red-700"
                          disabled={deleteAccount.isPending}
                        >
                          {deleteAccount.isPending ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Eliminando...
                            </>
                          ) : (
                            "Sí, eliminar cuenta"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}